/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GridColDef } from "@mui/x-data-grid";
import { FilterPayload, FilterClause, FilterType, Operator } from "./FilterDefinition";
import 'dayjs/locale/is';
dayjs.locale("is");

/** ─────────────────────────────────────────────────────────────────────────────
 *  Helpers / i18n
 *  ────────────────────────────────────────────────────────────────────────── */
const isValidISDate = (s?: string) => !!s && dayjs(s, "DD.MM.YYYY", true).isValid();
type MaybeFilterType = FilterType | "unknown";
const toFilterType = (x: MaybeFilterType): FilterType => (x === "unknown" ? "string" : x);

const OP_LABELS: Record<string, string> = {
  // string
  contains: "Inniheldur",
  equals: "Jafnt og",
  startsWith: "Byrjar á",
  endsWith: "Endar á",
  not: "Er ekki",
  in: "Er einhver af",
  // number
  eq: "Jafnt og",
  gt: "Stærra en",
  gte: "Stærra eða jafnt",
  lt: "Minna en",
  lte: "Minna eða jafnt",
  // date
  on: "Á",
  after: "Eftir",
  onOrAfter: "Á eða eftir",
  before: "Fyrir",
  onOrBefore: "Á eða fyrir",
  between: "Milli dagsetninga",
  // boolean
  is: "Er",
  isNot: "Er ekki",
  isNull: "Er tómt",
  isNotNull: "Er ekki tómt",
};

const fieldTypeFromCol = (c?: GridColDef): MaybeFilterType => {
  switch (c?.type) {
    case "number":
      return "number";
    case "date":
    case "dateTime":
      return "date";
    case "boolean":
      return "boolean";
    case "string":
    case undefined:
      return "string";
    default:
      return "unknown";
  }
};

const opsForType = (t: FilterType): Operator[] => {
  switch (t) {
    case "string":
      return ["contains", "equals", "startsWith", "endsWith", "not", "in"];
    case "number":
      return ["eq", "gt", "gte", "lt", "lte", "in"];
    case "date":
      return ["on", "after", "onOrAfter", "before", "onOrBefore", "between"];
    case "boolean":
      return ["is", "isNot", "isNull", "isNotNull"];
  }
};

const headerForField = (columns: GridColDef[], field: string) =>
  columns.find((c) => c.field === field)?.headerName ?? field;

const fmtOutDate = (d: Dayjs | null) => (d ? d.format("DD.MM.YYYY") : "");
const fmtChipDate = (s?: string) => (isValidISDate(s) ? dayjs(s, "DD.MM.YYYY").format("DD.MM.YYYY") : "—");

let NEXT_ID = 1;
const nextId = () => NEXT_ID++;

const splitInValue = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value.split(",").map((s) => s.trim()).filter(Boolean);
  }
  if (value !== undefined && value !== null) {
    return [String(value).trim()].filter(Boolean);
  }
  return [];
};

const parseInValue = (value: unknown, type: FilterType): string[] | number[] => {
  const values = splitInValue(value);
  if (type === "number") {
    return values.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  }
  return values;
};

const inValueToText = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
};

const normalizeClauseValue = (clause: FilterClause): FilterClause => {
  if (clause.operator !== "in") {
    return clause;
  }
  return {
    ...clause,
    value: parseInValue(clause.value, clause.type ?? "string"),
  };
};

const stripUiId = (clause: FilterClause & { id: number }): FilterClause => {
  const rest = { ...clause } as Partial<FilterClause & { id: number }>;
  delete rest.id;
  return rest as FilterClause;
};

/** Emptiness rules (what “counts” as a filter) */
const isClauseEmpty = (c: FilterClause): boolean => {
  const t = (c.type ?? "string") as FilterType;
  const op = c.operator as Operator;
  const v = c.value as any;

  if (t === "boolean") {
    if (op === "isNull" || op === "isNotNull") return false;
    return typeof v !== "boolean";
  }

  if (t === "number") {
    if (op === "in") return parseInValue(v, t).length === 0;
    return v === "" || v === null || Number.isNaN(v);
  }

  if (t === "date") {
    if (op === "between") {
      const startOk = isValidISDate(v?.start);
      const endOk = isValidISDate(v?.end);
      // require at least one valid bound
      return !(startOk || endOk);
    }
    // single date must be valid DD.MM.YYYY
    return !isValidISDate(v);
  }

  // string
  if (op === "in") return parseInValue(v, t).length === 0;
  return !v || String(v).trim() === "";
};


/** Strip UI id + drop empties */
const toAppliedPayload = (draft: (FilterClause & { id: number })[]): FilterPayload => ({
  logic: "and",
  clauses: draft
    .map(stripUiId)
    .map(normalizeClauseValue)
    .filter((c) => !isClauseEmpty(c)),
});

/** New helpers to enforce one filter per column */
const usedFields = (draft: (FilterClause & { id: number })[]) =>
  new Set(draft.map((d) => d.field));

const availableCols = (filterableCols: GridColDef[], draft: (FilterClause & { id: number })[]) =>
  filterableCols.filter((c) => !usedFields(draft).has(c.field));

const nextUnusedColumn = (
  filterableCols: GridColDef[],
  draft: (FilterClause & { id: number })[],
  afterField?: string
): GridColDef | undefined => {
  const used = usedFields(draft);
  const startIdx = afterField
    ? Math.max(0, filterableCols.findIndex((c) => c.field === afterField))
    : -1;

  // search forward from current
  for (let i = startIdx + 1; i < filterableCols.length; i++) {
    if (!used.has(filterableCols[i].field)) return filterableCols[i];
  }
  // wrap-around
  for (let i = 0; i <= startIdx; i++) {
    if (!used.has(filterableCols[i].field)) return filterableCols[i];
  }
  return undefined; // all used
};

/** ─────────────────────────────────────────────────────────────────────────────
 *  Props
 *  ────────────────────────────────────────────────────────────────────────── */
export type FiltersDialogProps = {
  /** Controls visibility. */
  open: boolean;
  /** Called when the dialog closes. We pass the *applied* (non-empty) payload. */
  onClose: (applied: FilterPayload) => void;

  /** Columns from the current grid (prefer visible + filterable). */
  columns: GridColDef[];

  /** Current applied model from the parent (used to seed when opening). */
  value: FilterPayload;

  /**
   * Optional index to focus when opening (e.g. when a chip was clicked).
   * If omitted, the last clause is focused; if there are none, a new one is created and focused.
   */
  initialFocusIndex?: number | null;
};

/** ─────────────────────────────────────────────────────────────────────────────
 *  Component
 *  ────────────────────────────────────────────────────────────────────────── */
const FiltersDialog: React.FC<FiltersDialogProps> = ({
  open,
  onClose,
  columns,
  value,
  initialFocusIndex = null,
}) => {
  const filterableCols = React.useMemo(
    () => (columns ?? []).filter((c) => c.filterable !== false),
    [columns]
  );

  // Local draft lives only while dialog is open
  const [draft, setDraft] = React.useState<(FilterClause & { id: number })[]>([]);
  const valueRefs = React.useRef(new Map<number, HTMLInputElement | null>());

  // Seed draft only on open (rising edge)
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      const source = Array.isArray(value?.clauses) ? value.clauses : [];

      // keep first occurrence per field (dedupe)
      const seen = new Set<string>();
      const unique = source.filter((c) => {
        const f = c.field ?? "";
        if (seen.has(f)) return false;
        seen.add(f);
        return true;
      });

      let seeded = unique.map((c, i) => ({ id: (c as any).id ?? nextId() ?? i, ...c }));

      if (seeded.length === 0) {
        const first = filterableCols[0];
        if (first) {
          const t = toFilterType(fieldTypeFromCol(first));
          const op = opsForType(t)[0];
          seeded = [
            {
              id: nextId(),
              field: first.field,
              type: t,
              operator: op,
              value:
                t === "date" && op === "between"
                  ? { start: "", end: "" }
                  : t === "boolean" && op === "is"
                    ? true
                    : "",
            },
          ];
        }
      }

      setDraft(seeded);

      // schedule focus to desired row after paint
      requestAnimationFrame(() => {
        const idx =
          initialFocusIndex === null || initialFocusIndex === undefined
            ? Math.max(0, seeded.length - 1)
            : Math.min(Math.max(initialFocusIndex, 0), Math.max(seeded.length - 1, 0));
        const targetId = seeded[idx]?.id;
        const el = valueRefs.current.get(targetId);
        el?.focus?.();
        el?.select?.();
      });
    }
    wasOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value?.clauses, initialFocusIndex, filterableCols]);

  // Close -> apply non-empty clauses
  const handleClose = () => {
    onClose(toAppliedPayload(draft));
  };

  // CRUD
  const add = (focusAfter = true) => {
    if (filterableCols.length === 0) return;
    const nextCol = nextUnusedColumn(filterableCols, draft);
    if (!nextCol) return; // nothing left to add

    const t = toFilterType(fieldTypeFromCol(nextCol));
    const op = opsForType(t)[0];
    const row = {
      id: nextId(),
      field: nextCol.field,
      type: t,
      operator: op,
      value:
        t === "date" && op === "between"
          ? { start: "", end: "" }
          : t === "boolean" && op === "is"
            ? true
            : "",
    } as FilterClause & { id: number };

    setDraft((prev) => {
      const next = [...prev, row];
      if (focusAfter) {
        requestAnimationFrame(() => {
          valueRefs.current.get(row.id)?.focus?.();
          valueRefs.current.get(row.id)?.select?.();
        });
      }
      return next;
    });
  };

  const remove = (id: number) =>
    setDraft((prev) => prev.filter((d) => d.id !== id));

  const clearAll = () => setDraft([]);

  const update = (id: number, patch: Partial<FilterClause>) =>
    setDraft((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  /** Enter: add next unused column (wrapping) and focus it; if none left, focus next row */
  const confirmAndNext = (rowIndex: number) => {
    setDraft((prev) => {
      const current = prev[rowIndex];
      const nextCol = nextUnusedColumn(filterableCols, prev, current?.field);
      if (!nextCol) {
        // no unused column left → just focus next row if exists
        const nextId_ = prev[rowIndex + 1]?.id;
        if (nextId_) {
          requestAnimationFrame(() => {
            valueRefs.current.get(nextId_)?.focus?.();
            valueRefs.current.get(nextId_)?.select?.();
          });
        }
        return prev;
      }
      const t = toFilterType(fieldTypeFromCol(nextCol));
      const op = opsForType(t)[0];
      const newRow: FilterClause & { id: number } = {
        id: nextId(),
        field: nextCol.field,
        type: t,
        operator: op,
        value:
          t === "date" && op === "between"
            ? { start: "", end: "" }
            : t === "boolean" && op === "is"
              ? true
              : "",
      };
      const next = [...prev, newRow];
      requestAnimationFrame(() => {
        valueRefs.current.get(newRow.id)?.focus?.();
        valueRefs.current.get(newRow.id)?.select?.();
      });
      return next;
    });
  };

  /** ───────────── Value Editors (keyboard & autoFocus ready) ───────────── */
  const ValueEditor = ({
    row,
    rowIndex,
  }: {
    row: FilterClause & { id: number };
    rowIndex: number;
  }) => {
    const currentCol =
      columns.find((c) => c.field === row.field) ?? filterableCols[0];
    const t: FilterType =
      row.type ?? toFilterType(fieldTypeFromCol(currentCol));

    // “Enter” moves to next clause (or adds next unused)
    const onEnter: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        confirmAndNext(rowIndex);
      }
    };

    // register input ref for focus management
    const registerInputRef = (el: HTMLInputElement | null) => {
      valueRefs.current.set(row.id, el);
    };

    if (t === "boolean" && (row.operator === "isNull" || row.operator === "isNotNull")) {
      return <Box sx={{ width: 220 }} />; // no value needed
    }

    if (t === "boolean") {
      return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id={`b-${row.id}`}>Gildi</InputLabel>
          <Select
            labelId={`b-${row.id}`}
            label="Gildi"
            value={row.value === true ? "true" : "false"}
            onChange={(e) => update(row.id, { value: e.target.value === "true" })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                confirmAndNext(rowIndex);
              }
            }}
            inputRef={(el) => registerInputRef(el as unknown as HTMLInputElement)}
            autoFocus
          >
            <MenuItem value="true">Satt</MenuItem>
            <MenuItem value="false">Ósatt</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (t === "date" && row.operator === "between") {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="is">
          <Stack direction="row" spacing={1} sx={{ width: 360 }}>
            <DatePicker
              label="Frá"
              value={
                (row.value as any)?.start && isValidISDate((row.value as any).start)
                  ? dayjs((row.value as any).start, "DD.MM.YYYY", true)
                  : null
              }
              onChange={(d) =>
                update(row.id, {
                  value: { start: fmtOutDate(d), end: (row.value as any)?.end || "" },
                })
              }
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  inputRef: registerInputRef,
                  autoFocus: true,
                  onKeyDown: onEnter,
                },
              }}
            />
            <DatePicker
              label="Til"
              value={
                (row.value as any)?.end && isValidISDate((row.value as any).end)
                  ? dayjs((row.value as any).end, "DD.MM.YYYY", true)
                  : null
              }
              onChange={(d) =>
                update(row.id, {
                  value: { start: (row.value as any)?.start || "", end: fmtOutDate(d) },
                })
              }
              format="DD.MM.YYYY"
              slotProps={{ textField: { size: "small", onKeyDown: onEnter } }}
            />
          </Stack>
        </LocalizationProvider>
      );
    }

    if (t === "date") {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="is">
          <DatePicker
            label="Dagsetning"
            value={
              typeof row.value === "string" && isValidISDate(row.value)
                ? dayjs(row.value, "DD.MM.YYYY", true)
                : null
            }
            onChange={(val) => update(row.id, { value: fmtOutDate(val) })}
            format="DD.MM.YYYY"
            slotProps={{
              textField: {
                size: "small",
                inputRef: registerInputRef,
                autoFocus: true,
                onKeyDown: onEnter,
              },
            }}
          />
        </LocalizationProvider>
      );
    }

    if (t === "number" && row.operator !== "in") {
      return (
        <TextField
          size="small"
          type="number"
          label="Gildi"
          value={row.value ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") return update(row.id, { value: "" });
            const n = Number(v);
            if (!Number.isNaN(n)) update(row.id, { value: n });
          }}
          onKeyDown={onEnter}
          inputRef={registerInputRef}
          autoFocus
          sx={{ width: 220 }}
        />
      );
    }

    if (row.operator === "in") {
      return (
        <TextField
          size="small"
          label="Gildi (aðskilin með kommu)"
          value={inValueToText(row.value)}
          onChange={(e) => update(row.id, { value: e.target.value })}
          onKeyDown={onEnter}
          inputRef={registerInputRef}
          autoFocus
          sx={{ width: 320 }}
        />
      );
    }

    // default string
    return (
      <TextField
        size="small"
        label="Gildi"
        value={(row.value ?? "") as string}
        onChange={(e) => update(row.id, { value: e.target.value })}
        onKeyDown={onEnter}
        inputRef={registerInputRef}
        autoFocus
        sx={{ width: 220 }}
      />
    );
  };

  /** Row block */
  const Row = ({
    row,
    index,
  }: {
    row: FilterClause & { id: number };
    index: number;
  }) => {
    const currentCol =
      columns.find((c) => c.field === row.field) ?? filterableCols[0];
    const t: FilterType =
      row.type ?? toFilterType(fieldTypeFromCol(currentCol));
    const ops = opsForType(t);

    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        {/* Field */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id={`f-${row.id}`}>Dálkur</InputLabel>
          <Select
            labelId={`f-${row.id}`}
            label="Dálkur"
            value={currentCol?.field ?? ""}
            onChange={(e) => {
              const nf = e.target.value as string;

              // block duplicates: if taken by another row, ignore
              if (draft.some((d) => d.id !== row.id && d.field === nf)) {
                return;
              }

              const ncol = columns.find((c) => c.field === nf);
              const nt = toFilterType(fieldTypeFromCol(ncol));
              const nop = opsForType(nt)[0];
              update(row.id, {
                field: nf,
                type: nt,
                operator: nop,
                value:
                  nt === "date" && nop === "between"
                    ? { start: "", end: "" }
                    : nt === "boolean" && nop === "is"
                      ? true
                      : "",
              });
              // shift focus to the value editor for speed
              requestAnimationFrame(() => {
                const el = valueRefs.current.get(row.id);
                el?.focus?.();
                el?.select?.();
              });
            }}
          >
            {filterableCols.map((c) => {
              const taken = draft.some((d) => d.id !== row.id && d.field === c.field);
              return (
                <MenuItem key={c.field} value={c.field} disabled={taken}>
                  {c.headerName ?? c.field}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Operator */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id={`o-${row.id}`}>Skilyrði</InputLabel>
          <Select
            labelId={`o-${row.id}`}
            label="Skilyrði"
            value={(ops.includes(row.operator) ? row.operator : ops[0]) as string}
            onChange={(e) => {
              const newOp = e.target.value as Operator;
              let nextVal: any = row.value;

              if (t === "date") {
                nextVal = newOp === "between" ? { start: "", end: "" } : "";
              } else if (t === "boolean") {
                nextVal =
                  newOp === "isNull" || newOp === "isNotNull"
                    ? undefined
                    : typeof row.value === "boolean"
                      ? row.value
                      : true;
              } else if (newOp === "in") {
                nextVal = inValueToText(row.value);
              } else {
                nextVal =
                  t === "number"
                    ? typeof row.value === "number"
                      ? row.value
                      : ""
                    : "";
              }

              update(row.id, { operator: newOp, value: nextVal });

              requestAnimationFrame(() => {
                const el = valueRefs.current.get(row.id);
                el?.focus?.();
                el?.select?.();
              });
            }}
          >
            {ops.map((o) => (
              <MenuItem key={o} value={o}>
                {OP_LABELS[o] ?? o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Value */}
        <ValueEditor row={row} rowIndex={index} />

        {/* Remove */}
        <Tooltip title="Fjarlægja">
          <IconButton color="error" onClick={() => remove(row.id)}>
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  const chips = (value?.clauses ?? []).map((c, idx) => {
    const header = headerForField(columns, c.field ?? "");
    const op = OP_LABELS[c.operator ?? ""] ?? c.operator ?? "";
    let v = "";
    if (c.type === "date") {
      if (c.operator === "between" && typeof c.value === "object" && c.value) {
        const vv = c.value as any;
        v = `${fmtChipDate(vv.start)} → ${fmtChipDate(vv.end)}`;
      } else if (typeof c.value === "string") v = fmtChipDate(c.value);
    } else if (Array.isArray(c.value)) v = c.value.join(" eða ");
    else if (c.value !== undefined && c.value !== null) v = String(c.value);
    return <Chip key={`${c.field}-${idx}`} label={`${header} ${op}${v ? ` ${v}` : ""}`} />;
  });

  const noColumns = filterableCols.length === 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Stilla síur
        <Box sx={{ ml: "auto" }} />
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>{chips}</Box>

        {noColumns ? (
          <Typography color="text.secondary">Engir síanlegir dálkar eru tiltækir.</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => add(true)}
                disabled={availableCols(filterableCols, draft).length === 0}
              >
                Ný sía
              </Button>
              <Button
                size="small"
                color="inherit"
                startIcon={<ClearAllIcon />}
                onClick={clearAll}
                disabled={draft.length === 0}
              >
                Hreinsa allt
              </Button>
            </Stack>

            <Divider />

            <Stack spacing={1.25}>
              {draft.length === 0 && (
                <Box sx={{ color: "text.secondary", fontSize: 14 }}>
                  Engin skilyrði — smelltu á <b>Ný sía</b> til að byrja.
                </Box>
              )}
              {draft.map((row, idx) => (
                <Row key={row.id} row={row} index={idx} />
              ))}
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(FiltersDialog);
